/**
 * NoEntitlementBanner — Phase 6.
 *
 * Rendered inline inside the New Booking modal when the backend returns
 * 422 ``no_eligible_entitlement``. Gives the admin a single-click
 * force-override toggle rather than making them dismiss and retry.
 *
 * The ``onConfirm`` callback re-submits the original form payload with
 * ``force: true`` appended. The banner disappears automatically when the
 * parent resets its error state (e.g. after a successful force-override
 * submission or when the operator changes the student or subject).
 */

import React from 'react';
import styled from 'styled-components';
import { Button } from '@/components/common';

interface NoEntitlementBannerProps {
  studentName?: string;
  /** Called when the operator clicks "Continue anyway" (re-submits with force=true). */
  onForceOverride: () => void;
  /** Called when the operator dismisses the banner (resets error state). */
  onDismiss: () => void;
  loading?: boolean;
  /** testId base; defaults to "candeza-bookings-no-entitlement-banner" */
  'data-testid'?: string;
}

const Banner = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: #92400e;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const Body = styled.p`
  color: #78350f;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin: 0;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const WarnIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

export const NoEntitlementBanner: React.FC<NoEntitlementBannerProps> = ({
  studentName,
  onForceOverride,
  onDismiss,
  loading = false,
  'data-testid': testId = 'candeza-bookings-no-entitlement-banner',
}) => (
  <Banner data-testid={testId} role="alert">
    <Title>
      <WarnIcon />
      No eligible entitlement found
    </Title>
    <Body>
      {studentName
        ? `${studentName} has no active package that covers this subject.`
        : 'The student has no active package that covers this subject.'}
      {' '}You can create this booking without consuming an entitlement — the session will be recorded as a force-override and visible to admins.
    </Body>
    <Actions>
      <Button
        variant="primary"
        size="sm"
        onClick={onForceOverride}
        disabled={loading}
        data-testid={`${testId}-confirm-btn`}
      >
        {loading ? 'Creating…' : 'Continue anyway'}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDismiss}
        disabled={loading}
        data-testid={`${testId}-dismiss-btn`}
      >
        Cancel
      </Button>
    </Actions>
  </Banner>
);

export default NoEntitlementBanner;

